import getLang from 'I18n/utils/getLang';
import { AppCard } from 'marketplace-ui-client-types/marketplace-search-service-v1/types';
import { useCallback } from 'react';
import { useTrackMarketplaceEvent } from '../../hooks/useTrackMarketplaceEvent';
import { getAppRecommendationTypes } from '../../utils/getAppRecommendationTypes';
import { createMarketplaceEventFactory } from '../index';
import type {
  MarketplaceEventProps,
  MarketplaceEventSchema,
  PartialPick,
  RequiredPick,
} from '../properties';

export type MarketplaceInteraction = MarketplaceEventSchema<
  RequiredPick<
    PartialPick<
      MarketplaceEventProps,
      | 'screen'
      | 'subscreen'
      | 'inAppOrPublic'
      | 'type'
      | 'embed'
      | 'installed'
      | 'hostApp'
      | 'sessionUrlParams'
      | 'action'
      | 'pageNumber'
      | 'search'
      | 'itemIndex'
      | 'totalResults'
      | 'value'
      | 'applicationId'
      | 'isRecommended'
      | 'category'
      | 'name'
      | 'preferredLanguage'
      | 'recommendationType'
      | 'tier'
      | 'percentScrolled'
      | 'section'
      | 'queryType'
      | 'filters'
      | 'sorts'
      | 'filterType'
      | 'filterValue'
      | 'listingId'
      | 'referrer'
      | 'sourceId'
      | 'resultCount'
      | 'collectionId'
      | 'slug'
      | 'companyName'
      | 'resultType'
      | 'resultIndex'
      | 'videoId'
      | 'videoWatchedDuration'
    >,
    'action'
  >
>;

export const getMarketplaceInteractionEvent =
  createMarketplaceEventFactory<MarketplaceInteraction>(
    'Marketplace',
    'Marketplace Interaction'
  );

/**
 * Marketplace click result item events
 */
type MarketplaceClickResultItemInteraction = MarketplaceEventSchema<
  RequiredPick<
    MarketplaceInteraction,
    'type' | 'itemIndex' | 'totalResults' | 'value' | 'search'
  >
>;

type AppMarketplaceClickResultItemInteraction = MarketplaceEventSchema<
  RequiredPick<
    MarketplaceClickResultItemInteraction,
    'applicationId' | 'isRecommended'
  >
>;

type SolutionsDirectoryClickResultItemInteraction = MarketplaceEventSchema<
  RequiredPick<
    MarketplaceClickResultItemInteraction,
    'itemIndex' | 'totalResults' | 'value' | 'search'
  >
>;

export const getMarketplaceClickResultItemEvent =
  createMarketplaceEventFactory<MarketplaceClickResultItemInteraction>(
    'Marketplace',
    'Marketplace Interaction'
  );

export function useTrackMarketplaceClickResultItem() {
  const trackMarketplaceEvent = useTrackMarketplaceEvent();

  const trackClickResultItem = useCallback(
    (eventProps: Omit<MarketplaceClickResultItemInteraction, 'action'>) => {
      trackMarketplaceEvent(
        getMarketplaceClickResultItemEvent({
          ...eventProps,
          action: 'click result item',
        })
      );
    },
    [trackMarketplaceEvent]
  );

  return { trackClickResultItem };
}

export const getAppMarketplaceClickResultItemEvent =
  createMarketplaceEventFactory<AppMarketplaceClickResultItemInteraction>(
    'Marketplace',
    'Marketplace Interaction'
  );

export function useTrackAppMarketplaceClickResultItem() {
  const trackMarketplaceEvent = useTrackMarketplaceEvent();

  const trackClickResultItem = useCallback(
    (
      result: Pick<AppCard, 'recommendation' | 'slug' | 'sourceId'>,
      eventProps: Omit<
        AppMarketplaceClickResultItemInteraction,
        | 'type'
        | 'action'
        | 'value'
        | 'applicationId'
        | 'isRecommended'
        | 'recommendationType'
      >
    ) => {
      trackMarketplaceEvent(
        getAppMarketplaceClickResultItemEvent({
          ...eventProps,
          type: 'integration',
          action: 'click result item',
          value: result.slug,
          applicationId: parseInt(result.sourceId, 10),
          isRecommended: !!result.recommendation,
          recommendationType: getAppRecommendationTypes(result) ?? undefined,
          preferredLanguage: eventProps.preferredLanguage || getLang(),
        })
      );
    },
    [trackMarketplaceEvent]
  );

  return { trackClickResultItem };
}

export const getSolutionsDirectoryClickResultItemEvent =
  createMarketplaceEventFactory<SolutionsDirectoryClickResultItemInteraction>(
    'Marketplace',
    'Marketplace Interaction'
  );

export function useTrackSolutionsDirectoryClickResultItem() {
  const trackMarketplaceEvent = useTrackMarketplaceEvent();

  const trackClickResultItem = useCallback(
    (
      result: { slug: string; tier: string },
      eventProps: Omit<
        SolutionsDirectoryClickResultItemInteraction,
        'type' | 'action' | 'value' | 'tier'
      >
    ) => {
      trackMarketplaceEvent(
        getSolutionsDirectoryClickResultItemEvent({
          ...eventProps,
          type: 'solution provider',
          action: 'click result item',
          value: result.slug,
          tier: result.tier,
        })
      );
    },
    [trackMarketplaceEvent]
  );

  return { trackClickResultItem };
}
